export const DEFAULT_MAX_AUDIO_SECONDS = 120;
const VOICE_RECORDING_MIME_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus", "audio/ogg"];

export async function readAudioDurationSeconds(file: File) {
  const objectUrl = URL.createObjectURL(file);

  try {
    const duration = await new Promise<number>((resolve, reject) => {
      const audio = document.createElement("audio");

      audio.preload = "metadata";
      audio.src = objectUrl;
      audio.onloadedmetadata = () => resolve(Number.isFinite(audio.duration) ? audio.duration : 0);
      audio.onerror = () => reject(new Error("Impossible de lire la durée de ce fichier audio."));
    });

    return Math.ceil(duration);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function validateVoiceMessageFile(file: File, maxDurationSeconds = DEFAULT_MAX_AUDIO_SECONDS) {
  if (!file.type.startsWith("audio/")) {
    throw new Error("Seuls les fichiers audio sont autorisés pour un message vocal.");
  }

  const durationSeconds = await readAudioDurationSeconds(file);

  if (durationSeconds <= 0) {
    throw new Error("La durée du message vocal est invalide.");
  }

  if (durationSeconds > maxDurationSeconds) {
    throw new Error(`Le message vocal ne doit pas dépasser ${maxDurationSeconds} secondes.`);
  }

  return durationSeconds;
}

export function formatAudioDuration(seconds: number) {
  const minutes = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const remainingSeconds = Math.max(0, seconds % 60)
    .toString()
    .padStart(2, "0");

  return `${minutes}:${remainingSeconds}`;
}

export function isVoiceRecordingSupported() {
  return typeof window !== "undefined"
    && typeof navigator !== "undefined"
    && typeof MediaRecorder !== "undefined"
    && typeof navigator.mediaDevices?.getUserMedia === "function";
}

export function resolveVoiceRecordingMimeType() {
  if (!isVoiceRecordingSupported()) {
    return "";
  }

  if (typeof MediaRecorder.isTypeSupported !== "function") {
    return "";
  }

  return VOICE_RECORDING_MIME_TYPES.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) ?? "";
}

function resolveVoiceRecordingExtension(mimeType: string) {
  if (mimeType.includes("mp4")) {
    return "m4a";
  }

  if (mimeType.includes("ogg")) {
    return "ogg";
  }

  return "webm";
}

export async function buildRecordedVoiceMessageFile(
  blob: Blob,
  maxDurationSeconds = DEFAULT_MAX_AUDIO_SECONDS,
  knownDurationSeconds?: number
) {
  const mimeType = blob.type || resolveVoiceRecordingMimeType() || "audio/webm";
  const file = new File([blob], `voice-note-${Date.now()}.${resolveVoiceRecordingExtension(mimeType)}`, {
    type: mimeType
  });

  if (!file.type.startsWith("audio/")) {
    throw new Error("Seuls les fichiers audio sont autorisés pour un message vocal.");
  }

  const durationSeconds =
    typeof knownDurationSeconds === "number" && Number.isFinite(knownDurationSeconds)
      ? Math.max(1, Math.ceil(knownDurationSeconds))
      : await validateVoiceMessageFile(file, maxDurationSeconds);

  if (durationSeconds > maxDurationSeconds) {
    throw new Error(`Le message vocal ne doit pas dépasser ${maxDurationSeconds} secondes.`);
  }

  return {
    file,
    durationSeconds
  };
}
